import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Mega Menu hover commit delay
 *
 * 这个延迟只用于触发 MegaMenuHoverEvent，
 * 不会阻止 Mega Menu 本身立即显示。
 */
const HOVER_COMMIT_DELAY_MS = 50;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem
 * @property {HTMLElement | null} activeOverflowItem
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuMutationObserver = null;
  #hoverDispatchTimer = undefined;
  #pointerIdleTimer = undefined;

  #lastPointer = {
    x: 0,
    y: 0,
  };

  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    /*
     * 不等待 document load。
     *
     * Header Menu 在组件加载完成后立即可以响应 hover。
     */
    window.addEventListener(
      'resize',
      this.#resizeListener
    );

    this.addEventListener(
      'keydown',
      this.#onKeydown
    );

    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      'resize',
      this.#resizeListener
    );

    this.removeEventListener(
      'keydown',
      this.#onKeydown
    );

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    if (this.#state.activeItem) {
      this.#stopPointerTracking(
        this.#state.activeItem
      );
    }

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    this.#cleanupMutationObserver();

    clearTimeout(
      this.#hoverDispatchTimer
    );

    this.#hoverDispatchTimer =
      undefined;

    clearTimeout(
      this.#pointerIdleTimer
    );

    this.#pointerIdleTimer =
      undefined;
  }

  /**
   * =====================================================
   * Resize
   * =====================================================
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();

    const activeItem =
      this.#state.activeItem;

    if (!activeItem) return;

    const submenu =
      findSubmenu(activeItem) ||
      (
        activeItem.closest('[slot="more"]')
          ? this.overflowMenu
          : null
      );

    if (!submenu) return;

    this.#scheduleHeightUpdate(
      submenu,
      activeItem
    );
  }, 100);

  /**
   * =====================================================
   * Overflow submenu leave
   * =====================================================
   */
  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * =====================================================
   * Keyboard
   * =====================================================
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const item =
      this.#state.activeItem;

    if (!item) return;

    const control =
      this.#focusTargetAfterClose(item);

    this.#deactivate(
      item,
      {
        force: true,
      }
    );

    control.focus();
  };

  /**
   * =====================================================
   * Click toggle
   * =====================================================
   */
  toggle = (event) => {
    const button =
      event.target;

    if (!(button instanceof HTMLElement)) {
      return;
    }

    const item =
      findMenuItem(button);

    if (!item) return;

    const isExpanded =
      button.getAttribute(
        'aria-expanded'
      ) === 'true';

    if (isExpanded) {
      this.#deactivate(
        this.#state.activeItem ?? item,
        {
          force: true,
        }
      );

      const focusTarget =
        this.#focusTargetAfterClose(item);

      if (focusTarget !== button) {
        focusTarget.focus();
      }
    } else {
      this.activate(event);
    }
  };

  /**
   * =====================================================
   * Focus target
   * =====================================================
   */
  #focusTargetAfterClose(item) {
    const listItem =
      item.closest(
        '.menu-list__list-item'
      );

    const slot =
      listItem instanceof HTMLElement
        ? listItem.slot
        : '';

    if (
      slot === 'overflow' ||
      slot === 'more'
    ) {
      const moreButton =
        this.querySelector(
          '[slot="more"] [ref="menuitem"]'
        );

      if (
        moreButton instanceof HTMLElement
      ) {
        return moreButton;
      }
    }

    return this.#expandableFor(item);
  }

  /**
   * =====================================================
   * Expandable element
   * =====================================================
   */
  #expandableFor(item) {
    const listItem =
      item.closest(
        '.menu-list__list-item'
      );

    const disclosure =
      listItem?.querySelector(
        '[ref="disclosure[]"]'
      );

    return disclosure instanceof HTMLElement
      ? disclosure
      : item;
  }

  /**
   * =====================================================
   * Pointer tracking
   * =====================================================
   */
  #onPointerMove = (event) => {
    const activeLink =
      this.#state.activeItem;

    if (!activeLink) return;

    this.#lastPointer.x =
      event.clientX;

    this.#lastPointer.y =
      event.clientY;

    const moving =
      Math.abs(event.movementX) >= 1 ||
      Math.abs(event.movementY) >= 1;

    activeLink.dataset.safetyBox =
      `${moving}`;

    clearTimeout(
      this.#pointerIdleTimer
    );

    if (moving) {
      this.#pointerIdleTimer =
        setTimeout(() => {
          if (this.#state.activeItem) {
            this.#state.activeItem.dataset.safetyBox =
              'false';

            this.#reconcilePointerTarget();
          }
        }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * =====================================================
   * Pointer target reconciliation
   * =====================================================
   */
  #reconcilePointerTarget() {
    const {
      x,
      y,
    } = this.#lastPointer;

    requestAnimationFrame(() => {
      const target =
        document.elementFromPoint(
          x,
          y
        );

      if (!target) return;

      const listItem =
        target.closest(
          '.menu-list__list-item'
        );

      if (
        listItem &&
        !listItem.contains(
          this.#state.activeItem
        )
      ) {
        listItem.dispatchEvent(
          new PointerEvent(
            'pointerenter',
            {
              bubbles: false,
            }
          )
        );
      }
    });
  }

  /**
   * =====================================================
   * Start pointer tracking
   * =====================================================
   */
  #startPointerTracking(
    item,
    previousItem
  ) {
    if (previousItem) {
      this.#stopPointerTracking(
        previousItem
      );
    } else {
      document.body.addEventListener(
        'pointermove',
        this.#onPointerMove
      );
    }

    const rect =
      item.getBoundingClientRect();

    const isOverlap =
      this.headerComponent?.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const boundary =
      isOverlap
        ? this.headerComponent?.querySelector(
            '.header__row--top'
          )
        : this.headerComponent;

    item.style.setProperty(
      '--box-height',
      `${
        (boundary?.getBoundingClientRect()
          .bottom ?? 0) - rect.top
      }px`
    );
  }

  /**
   * =====================================================
   * Stop pointer tracking
   * =====================================================
   */
  #stopPointerTracking(item) {
    clearTimeout(
      this.#pointerIdleTimer
    );

    this.#pointerIdleTimer =
      undefined;

    item.style.removeProperty(
      '--box-height'
    );

    delete item.dataset.safetyBox;
  }

  /**
   * =====================================================
   * Overflow menu
   * =====================================================
   */
  get overflowMenu() {
    return this.refs.overflowMenu
      ?.shadowRoot
      ?.querySelector(
        '[part="overflow"]'
      );
  }

  /**
   * =====================================================
   * Overflow list hovered
   * =====================================================
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu
        ?.shadowRoot
        ?.querySelector(
          '[part="overflow-list"]'
        )
        ?.matches(':hover') ?? false
    );
  }

  /**
   * =====================================================
   * First overflow item
   * =====================================================
   */
  #getFirstOverflowMenuItem() {
    const menuItem =
      this.refs.overflowMenu?.querySelector(
        '[slot="overflow"] [ref="menuitem"]'
      );

    return menuItem instanceof HTMLElement
      ? menuItem
      : null;
  }

  /**
   * =====================================================
   * Header
   * =====================================================
   */
  get headerComponent() {
    return this.closest(
      'header-component'
    );
  }

  /**
   * =====================================================
   * ACTIVATE
   * =====================================================
   */
  activate = (event) => {
    if (
      !(event.target instanceof Element) ||
      !this.headerComponent
    ) {
      return;
    }

    const item =
      findMenuItem(event.target);

    if (!item) return;

    /*
     * 如果本身已经是当前菜单，
     * 不重新初始化。
     *
     * 这是解决第一次 hover 闪一下的关键之一。
     */
    if (
      item === this.#state.activeItem
    ) {
      return;
    }

    const listItem =
      item.closest(
        '.menu-list__list-item'
      );

    const slot =
      listItem instanceof HTMLElement
        ? listItem.slot
        : '';

    const isMoreTrigger =
      slot === 'more';

    const isDefaultSlot =
      slot === '';

    const overflowItem =
      isMoreTrigger
        ? this.#getFirstOverflowMenuItem()
        : null;

    this.dataset.overflowExpanded =
      (!isDefaultSlot).toString();

    const previousItem =
      this.#state.activeItem;

    const previousOverflowItem =
      this.#state.activeOverflowItem;

    /**
     * ===================================================
     * 关闭旧菜单状态
     * ===================================================
     */
    if (previousItem) {
      this.#expandableFor(
        previousItem
      ).ariaExpanded =
        'false';

      const previousSubmenu =
        findSubmenu(
          previousItem
        );

      if (previousSubmenu) {
        previousSubmenu.inert =
          true;
      }

      this.#stopPointerTracking(
        previousItem
      );
    }

    if (
      previousOverflowItem &&
      previousOverflowItem !== previousItem
    ) {
      this.#expandableFor(
        previousOverflowItem
      ).ariaExpanded =
        'false';

      const previousOverflowSubmenu =
        findSubmenu(
          previousOverflowItem
        );

      if (previousOverflowSubmenu) {
        previousOverflowSubmenu.inert =
          true;
      }
    }

    /**
     * ===================================================
     * 设置当前 item
     * ===================================================
     */
    this.#state.activeItem =
      item;

    this.#state.activeOverflowItem =
      overflowItem;

    this.ariaExpanded =
      'true';

    this.#expandableFor(
      item
    ).ariaExpanded =
      'true';

    if (
      overflowItem &&
      overflowItem !== item
    ) {
      this.#expandableFor(
        overflowItem
      ).ariaExpanded =
        'true';

      const activeOverflowSubmenu =
        findSubmenu(
          overflowItem
        );

      if (activeOverflowSubmenu) {
        activeOverflowSubmenu.inert =
          false;
      }
    }

    /**
     * ===================================================
     * 找 submenu
     * ===================================================
     */
    const overflowItemSubmenu =
      isMoreTrigger
        ? findSubmenu(
            overflowItem
          )
        : null;

    let submenu =
      findSubmenu(item) ||
      (
        overflowItemSubmenu
          ? this.overflowMenu
          : null
      );

    const hasSubmenu =
      Boolean(submenu);

    if (
      !hasSubmenu &&
      !isDefaultSlot
    ) {
      submenu =
        this.overflowMenu;
    }

    /**
     * ===================================================
     * 立即打开 submenu
     * ===================================================
     */
    if (submenu) {
      /*
       * 清除之前的 hover timer。
       */
      clearTimeout(
        this.#hoverDispatchTimer
      );

      this.#hoverDispatchTimer =
        undefined;

      const committedItem =
        item;

      /**
       * 注意：
       *
       * Mega Menu 本身现在立即 active。
       *
       * 这里的 50ms 只控制
       * MegaMenuHoverEvent。
       */
      if (event instanceof FocusEvent) {
        this.dispatchEvent(
          new MegaMenuHoverEvent()
        );
      } else {
        this.#hoverDispatchTimer =
          setTimeout(() => {
            this.#hoverDispatchTimer =
              undefined;

            if (
              this.#state.activeItem ===
              committedItem
            ) {
              this.dispatchEvent(
                new MegaMenuHoverEvent()
              );
            }
          },
          HOVER_COMMIT_DELAY_MS
          );
      }

      /**
       * =================================================
       * 关键：
       *
       * submenu 只在这里 active 一次。
       * =================================================
       */
      submenu.dataset.active =
        '';

      submenu.inert =
        false;

      /*
       * 立即显示。
       */
      this.style.setProperty(
        '--submenu-opacity',
        '1'
      );

      /**
       * 先同步计算一次高度。
       */
      const initialHeight =
        this.#calculateSubmenuHeight(
          submenu,
          item,
          isDefaultSlot,
          hasSubmenu
        );

      this.headerComponent.style.setProperty(
        '--submenu-height',
        `${initialHeight}px`
      );

      this.#setFullOpenHeaderHeight(
        initialHeight,
        this.#getHeaderVisibleHeight()
      );

      /**
       * =================================================
       * 延后一帧重新计算高度
       *
       * 这里只改变高度，
       * 不再改变 submenu active 状态。
       * =================================================
       */
      this.#scheduleHeightUpdate(
        submenu,
        item
      );

      /**
       * =================================================
       * MutationObserver
       *
       * 只负责高度。
       * 不负责重新打开/关闭菜单。
       * =================================================
       */
      this.#cleanupMutationObserver();

      this.#submenuMutationObserver =
        new MutationObserver(() => {
          if (
            this.#state.activeItem !==
            item
          ) {
            return;
          }

          this.#scheduleHeightUpdate(
            submenu,
            item
          );
        });

      this.#submenuMutationObserver.observe(
        submenu,
        {
          childList: true,
          subtree: true,
        }
      );

      /**
       * 防止 observer 长时间运行。
       */
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 1000);
    }

    /**
     * ===================================================
     * 没有 submenu
     * ===================================================
     */
    let finalHeight =
      submenu
        ? this.#calculateSubmenuHeight(
            submenu,
            item,
            isDefaultSlot,
            hasSubmenu
          )
        : 0;

    if (!submenu) {
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight,
      this.#getHeaderVisibleHeight()
    );

    this.style.setProperty(
      '--submenu-opacity',
      submenu
        ? '1'
        : '0'
    );

    /**
     * 开始鼠标 tracking
     */
    this.#startPointerTracking(
      item,
      previousItem
    );
  };

  /**
   * =====================================================
   * Calculate submenu height
   * =====================================================
   */
  #calculateSubmenuHeight(
    submenu,
    item,
    isDefaultSlot,
    hasSubmenu
  ) {
    if (!submenu) {
      return 0;
    }

    let height =
      submenu.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu?.offsetHeight ||
          0;

        height =
          Math.max(
            overflowHeight,
            overflowListHeight
          );
      } else {
        height =
          overflowListHeight;
      }
    }

    return height;
  }

  /**
   * =====================================================
   * Schedule height update
   *
   * 这里只重新计算高度。
   *
   * 不重新设置：
   * - dataset.active
   * - inert
   * - aria-expanded
   * - activeItem
   *
   * 避免第一次 hover 闪烁。
   * =====================================================
   */
  #scheduleHeightUpdate(
    submenu,
    item
  ) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (
          this.#state.activeItem !==
          item
        ) {
          return;
        }

        if (
          !submenu ||
          !submenu.isConnected
        ) {
          return;
        }

        const listItem =
          item.closest(
            '.menu-list__list-item'
          );

        const slot =
          listItem instanceof HTMLElement
            ? listItem.slot
            : '';

        const isDefaultSlot =
          slot === '';

        const hasSubmenu =
          Boolean(
            findSubmenu(item)
          );

        const height =
          this.#calculateSubmenuHeight(
            submenu,
            item,
            isDefaultSlot,
            hasSubmenu
          );

        this.headerComponent?.style.setProperty(
          '--submenu-height',
          `${height}px`
        );

        this.#setFullOpenHeaderHeight(
          height,
          this.#getHeaderVisibleHeight()
        );
      });
    });
  }

  /**
   * =====================================================
   * DEACTIVATE
   * =====================================================
   */
  deactivate(event) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const menu =
      findSubmenu(
        this.#state.activeItem
      );

    const isMovingWithinItem =
      event.relatedTarget instanceof Node &&
      event.target instanceof Element &&
      event.target.contains(
        event.relatedTarget
      );

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      menu?.contains(
        document.activeElement
      );

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(
        event.relatedTarget
      );

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element &&
      Boolean(
        event.relatedTarget.closest(
          '[slot="overflow"]'
        )
      );

    if (
      isMovingWithinItem ||
      isMovingWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu
    ) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(
          this.#state.activeItem
        );
      }

      return;
    }

    this.#deactivate();
  }

  /**
   * =====================================================
   * Real deactivate
   * =====================================================
   */
  #deactivate = (
    item = this.#state.activeItem,
    {
      force = false,
    } = {}
  ) => {
    if (
      !item ||
      item !== this.#state.activeItem
    ) {
      return;
    }

    if (
      !force &&
      (
        this.overflowListHovered ||
        this.overflowMenu?.matches(':hover')
      )
    ) {
      return;
    }

    clearTimeout(
      this.#hoverDispatchTimer
    );

    this.#hoverDispatchTimer =
      undefined;

    /**
     * 清除高度
     */
    this.headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.#setFullOpenHeaderHeight(
      0,
      0
    );

    /**
     * 隐藏
     */
    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded =
      'false';

    const submenu =
      findSubmenu(item) ||
      (
        item.closest('[slot="more"]')
          ? this.overflowMenu
          : null
      );

    const activeOverflowItem =
      this.#state.activeOverflowItem;

    /**
     * 停止 tracking
     */
    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(
      item
    );

    /**
     * 清除状态
     */
    this.#state.activeItem =
      null;

    this.#state.activeOverflowItem =
      null;

    this.ariaExpanded =
      'false';

    this.#expandableFor(
      item
    ).ariaExpanded =
      'false';

    if (
      activeOverflowItem &&
      activeOverflowItem !== item
    ) {
      this.#expandableFor(
        activeOverflowItem
      ).ariaExpanded =
        'false';

      const activeOverflowSubmenu =
        findSubmenu(
          activeOverflowItem
        );

      if (activeOverflowSubmenu) {
        activeOverflowSubmenu.inert =
          true;
      }
    }

    /**
     * 关闭 submenu
     */
    if (submenu) {
      delete submenu.dataset.active;

      submenu.inert =
        true;
    }

    this.#cleanupMutationObserver();
  };

  /**
   * =====================================================
   * Overflow list height
   * =====================================================
   */
  #getOverflowListLinksHeight() {
    const slottedMenuLinks =
      this.overflowMenu
        ?.querySelector('slot')
        ?.assignedElements();

    if (!slottedMenuLinks) {
      return (
        this.overflowMenu?.offsetHeight ||
        0
      );
    }

    const mapSubmenus =
      (callback) => {
        slottedMenuLinks.forEach(
          (link) => {
            const submenu =
              link.querySelector(
                '[ref="submenu[]"]'
              );

            if (submenu) {
              callback(submenu);
            }
          }
        );
      };

    mapSubmenus(
      (submenu) => {
        submenu.style.setProperty(
          'display',
          'none'
        );
      }
    );

    const height =
      this.overflowMenu?.offsetHeight ||
      0;

    mapSubmenus(
      (submenu) => {
        submenu.style.removeProperty(
          'display'
        );
      }
    );

    return height;
  }

  /**
   * =====================================================
   * Header visible height
   * =====================================================
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) {
      return 0;
    }

    const isOverlapSituation =
      this.headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    return (
      isOverlapSituation &&
      this.headerComponent.offsetHeight > 0
    )
      ? (
          this.headerComponent.querySelector(
            '.header__row--top'
          )?.offsetHeight ?? 0
        )
      : this.headerComponent.offsetHeight;
  }

  /**
   * =====================================================
   * Full open header height
   * =====================================================
   */
  #setFullOpenHeaderHeight(
    submenuHeight,
    headerVisibleHeight
  ) {
    if (!this.headerComponent) {
      return;
    }

    const nothingToOpen =
      submenuHeight === 0;

    const fullOpenHeaderHeight =
      nothingToOpen
        ? 0
        : submenuHeight +
          headerVisibleHeight;

    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * =====================================================
   * Cleanup observer
   * =====================================================
   */
  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();

    this.#submenuMutationObserver =
      null;
  }
}

/**
 * =====================================================
 * Register custom element
 * =====================================================
 */
if (!customElements.get('header-menu')) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

/**
 * =====================================================
 * Find menu item
 * =====================================================
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) {
    return null;
  }

  const listItem =
    element.closest(
      '.menu-list__list-item'
    ) ?? element;

  return listItem.querySelector(
    '[ref="menuitem"]'
  );
}

/**
 * =====================================================
 * Find submenu
 * =====================================================
 */
function findSubmenu(element) {
  const listItem =
    element?.closest?.(
      '.menu-list__list-item'
    );

  const submenu =
    listItem?.querySelector(
      ':scope > [ref="submenu[]"]'
    );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}